import type { Status } from '@tabel/shared/schemas/timesheets';
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronsUpDown,
  type LucideIcon,
  Maximize2,
  Minimize2,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react';
import {
  type ButtonHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';

/* Базовые компоненты по дизайн-системе (timesheet/design-system, см. web/DESIGN.md).
   Один интерфейс для компьютера и телефона: на телефоне (max-sm) цели нажатия не меньше 40 px, поля 16 px. */

type Variant = 'primary' | 'soft' | 'secondary' | 'ghost' | 'danger' | 'dangerText';
const VARIANT: Record<Variant, string> = {
  primary: 'bg-accent text-white hover:bg-accent-hover disabled:bg-accent/40',
  // Мягкая акцентная — только призыв в боковом меню (дизайн-система, «Каркас экрана»).
  soft: 'border border-accent/20 bg-accent-soft text-accent hover:border-accent/35',
  secondary:
    'border border-line bg-surface text-ink hover:border-line-strong hover:bg-subtle disabled:text-ink-3 disabled:hover:bg-surface',
  ghost: 'text-ink-2 hover:bg-subtle hover:text-ink disabled:text-ink-3',
  danger: 'bg-bad text-white hover:bg-bad/90 disabled:bg-bad/40',
  // Опасное действие в списке — красная текстовая кнопка (дизайн-система, Button: «В списках опасное действие —
  // красный пункт меню или красная текстовая кнопка»); сплошная красная — только в окне подтверждения.
  dangerText: 'text-bad hover:bg-bad-soft disabled:text-ink-3',
};

// На телефоне кнопка — цель нажатия не меньше 40 px (size-tap), маленькая — 32 px.
const SIZE = {
  sm: 'h-7 px-2.5 text-[12.5px] gap-1.5 max-sm:h-8',
  md: 'h-8 px-3 text-[13px] gap-2 max-sm:h-10',
  lg: 'h-10 px-4 text-[14px] gap-2',
} as const;

export function Button({
  variant = 'secondary',
  size = 'md',
  icon: Icon,
  className = '',
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
}) {
  const sz = SIZE[size];
  return (
    <button
      type="button"
      className={`inline-flex shrink-0 items-center justify-center rounded-control font-medium whitespace-nowrap transition-[color,background-color,border-color,transform] duration-150 ease-out enabled:active:scale-[0.97] disabled:cursor-not-allowed motion-reduce:transition-none motion-reduce:active:scale-100 ${sz} ${VARIANT[variant]} ${className}`}
      {...rest}
    >
      {Icon && <Icon size={16} strokeWidth={1.75} aria-hidden />}
      {children}
    </button>
  );
}

export function IconButton({
  icon: Icon,
  label,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { icon: LucideIcon; label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-control text-ink-2 transition-colors hover:bg-subtle hover:text-ink max-sm:h-10 max-sm:w-10 ${className}`}
      {...rest}
    >
      <Icon size={16} strokeWidth={1.75} aria-hidden />
    </button>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-card border border-line bg-surface shadow-card ${className}`}>{children}</div>
  );
}

export function CardHeader({
  title,
  description,
  actions,
  id,
  headingRef,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** `id` заголовка: карточка-раздел ссылается на него (`aria-labelledby`). */
  id?: string;
  /** Фокус на заголовке после действия: заголовок получает `tabIndex={-1}`. */
  headingRef?: Ref<HTMLHeadingElement>;
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 max-sm:flex-wrap max-sm:px-4">
      <div className="min-w-0">
        <h2
          id={id}
          ref={headingRef}
          tabIndex={headingRef ? -1 : undefined}
          className={`text-[15px] font-semibold break-words text-ink ${headingRef ? 'outline-none' : ''}`}
        >
          {title}
        </h2>
        {description && <p className="mt-0.5 text-[13px] text-ink-2">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

type Tone = 'neutral' | 'accent' | 'ok' | 'warn' | 'bad';
const TONE: Record<Tone, string> = {
  neutral: 'bg-subtle text-ink-2 ring-line',
  accent: 'bg-accent-soft text-accent ring-accent/20',
  ok: 'bg-ok-soft text-ok ring-ok/20',
  warn: 'bg-warn-soft text-warn ring-warn/20',
  bad: 'bg-bad-soft text-bad ring-bad/20',
};

export function Badge({
  tone = 'neutral',
  children,
  dot,
}: { tone?: Tone; children: ReactNode; dot?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[12px] font-medium whitespace-nowrap ring-1 ring-inset ${TONE[tone]}`}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}

const STATUS: Record<Status, { tone: Tone; label: string }> = {
  draft: { tone: 'neutral', label: 'Черновик' },
  completed: { tone: 'accent', label: 'Завершён' },
  returned: { tone: 'warn', label: 'Возвращён' },
  signed: { tone: 'ok', label: 'Подписан' },
};

export function StatusBadge({ status }: { status: Status }) {
  const s = STATUS[status];
  return (
    <Badge tone={s.tone} dot>
      {s.label}
    </Badge>
  );
}

/** Окно на телефоне выезжает снизу на всю ширину (дизайн-система, «Один интерфейс»), содержимое прокручивается. */
const DIALOG_CLS =
  'm-auto max-h-[90dvh] max-w-[95vw] overflow-y-auto overscroll-contain rounded-sheet border border-line bg-surface p-0 text-ink shadow-pop max-sm:mx-0 max-sm:mt-auto max-sm:mb-0 max-sm:max-h-[92dvh] max-sm:w-full max-sm:max-w-full max-sm:rounded-b-none max-sm:border-x-0 max-sm:border-b-0';

/**
 * Нативный <dialog> по флагу `open` (дизайн-система, «Слои и Esc»):
 * - при открытии фокус — внутрь окна: на `[data-autofocus]`, иначе на первый элемент управления;
 * - Esc закрывает только это окно — один слой за нажатие: панель и меню под окном Esc не получают;
 * - после закрытия, если фокус потерян, — на элемент, с которого окно открыли (кнопка, пункт меню → его кнопка «…»).
 */
function useNativeDialog(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);
  // Событие close приходит и при программном закрытии — onClose зовём только если закрыл пользователь (Esc).
  const programmatic = useRef(false);
  // Элемент, с которого открыли окно: запоминается до showModal.
  const opener = useRef<Element | null>(null);
  const latest = useRef(onClose);
  latest.current = onClose;
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      opener.current = document.activeElement;
      d.showModal();
    }
    if (!open && d.open) {
      programmatic.current = true;
      d.close();
    }
  }, [open]);
  useEffect(() => {
    const d = ref.current;
    if (open) {
      if (d && !d.contains(document.activeElement)) {
        (
          d.querySelector<HTMLElement>('[data-autofocus]') ?? d.querySelector<HTMLElement>(FOCUSABLE)
        )?.focus();
      }
      return;
    }
    // Браузер сам возвращает фокус после close(); если фокус потерян — на элемент, открывший окно.
    const now = document.activeElement;
    const back = opener.current;
    opener.current = null;
    if ((!now || now === document.body) && back instanceof HTMLElement && back.isConnected) back.focus();
  }, [open]);
  return {
    ref,
    onClose: () => {
      if (programmatic.current) {
        programmatic.current = false;
        return;
      }
      latest.current();
    },
    // Esc — только это окно: обрабатывается здесь одинаково во всех браузерах, дальше событие не идёт.
    onKeyDown: (e: ReactKeyboardEvent<HTMLDialogElement>) => {
      if (e.key !== 'Escape' || e.nativeEvent.isComposing || e.defaultPrevented) return;
      e.preventDefault();
      e.stopPropagation();
      latest.current();
    },
  };
}

/**
 * Модальное окно на нативном <dialog>: заголовок, пояснение, крестик; Esc закрывает.
 * На телефоне выезжает снизу на всю ширину (дизайн-система, «Один интерфейс»), содержимое прокручивается.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  width = 'w-[520px]',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  width?: string;
}) {
  const dialog = useNativeDialog(open, onClose);
  return (
    <dialog {...dialog} aria-label={title} className={`${width} ${DIALOG_CLS}`}>
      {open && (
        <div className="p-6 max-sm:px-4 max-sm:pt-5 max-sm:pb-[max(20px,env(safe-area-inset-bottom))]">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-[16px] font-semibold">{title}</h2>
              {description && <p className="mt-1 text-[13px] text-ink-2">{description}</p>}
            </div>
            <IconButton icon={X} label="Закрыть" onClick={onClose} className="-mt-1 -mr-2" />
          </div>
          {children}
        </div>
      )}
    </dialog>
  );
}

const ALERT_TONE = {
  bad: 'bg-bad-soft text-bad',
  warn: 'bg-warn-soft text-warn',
} as const;

/**
 * Окно-вопрос (дизайн-система, ConfirmDialog): без крестика, слева иконка в цветном квадрате (`bad` — удаление,
 * `warn` — несохранённое), заголовок — вопрос с объектом, текст — последствия. Кнопки — `children`, слева направо
 * как в подвале Modal (вторичное → распорка → «Отмена» → главное); на телефоне — во всю ширину.
 * Фокус при открытии — на кнопке с `data-autofocus`, иначе на первой. Esc — `onClose`.
 */
export function AlertDialog({
  open,
  onClose,
  title,
  description,
  icon: Icon = Trash2,
  tone = 'bad',
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: ReactNode;
  icon?: LucideIcon;
  tone?: keyof typeof ALERT_TONE;
  children: ReactNode;
}) {
  const dialog = useNativeDialog(open, onClose);
  return (
    <dialog {...dialog} role="alertdialog" aria-label={title} className={`w-[440px] ${DIALOG_CLS}`}>
      {open && (
        <div className="p-6 max-sm:px-4 max-sm:pt-5 max-sm:pb-[max(20px,env(safe-area-inset-bottom))]">
          <div className="mb-5 flex items-start gap-3">
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] ${ALERT_TONE[tone]}`}
            >
              <Icon size={18} strokeWidth={1.75} aria-hidden />
            </span>
            <div className="min-w-0">
              <h2 className="text-[16px] font-semibold break-words">{title}</h2>
              <p className="mt-1 text-[13px] text-ink-2">{description}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 max-sm:[&>button]:flex-auto">
            {children}
          </div>
        </div>
      )}
    </dialog>
  );
}

/**
 * Подтверждение необратимого (дизайн-система, ConfirmDialog): без крестика, красная иконка слева, вопрос с объектом,
 * последствия числами. Кнопки справа: «Отмена» — первая, в фокусе при открытии; действие «глагол + объект».
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  busy,
  icon = Trash2,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  busy?: boolean;
  icon?: LucideIcon;
}) {
  return (
    <AlertDialog open={open} onClose={onClose} title={title} description={description} icon={icon}>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="danger" disabled={busy} onClick={onConfirm}>
        {confirmLabel}
      </Button>
    </AlertDialog>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: поле передаётся дочерним элементом
    <label className="mb-4 block">
      <span className="mb-1.5 block text-[13px] font-medium text-ink">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[12px] text-ink-3">{hint}</span>}
    </label>
  );
}

/** Поле без ширины — ширину задаёт место использования. */
export const fieldCls =
  'h-9 rounded-control border border-line bg-surface px-3 text-[14px] text-ink placeholder:text-ink-3 transition-colors hover:border-line-strong focus:border-accent focus:ring-2 focus:ring-accent/15 focus:outline-none max-sm:h-10 max-sm:text-base';
export const inputCls = `w-full ${fieldCls}`;
export const textareaCls =
  'w-full rounded-control border border-line bg-surface px-3 py-2 text-[14px] text-ink placeholder:text-ink-3 transition-colors hover:border-line-strong focus:border-accent focus:ring-2 focus:ring-accent/15 focus:outline-none max-sm:text-base';

export function PageHeader({
  title,
  titleAside,
  description,
  actions,
  meta,
}: {
  title: ReactNode;
  /** Рядом с заголовком, вне `<h1>`: меню «…» действий с табелем (contracts/ui-panel.md, «Шапка табеля»). */
  titleAside?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
}) {
  // При открытой правой панели лист уже — действия под заголовком, как на телефоне (contracts/ui-panel.md).
  const stacked = useSidePanelOpen();
  return (
    // До 900 px действия переносятся под заголовок; главная кнопка — последняя (дизайн-система, PageHeader).
    <div
      data-page-header
      data-stacked={stacked ? 'yes' : 'no'}
      className={`flex items-start justify-between ${stacked ? 'flex-col gap-4' : 'gap-6 max-tablet:flex-col max-tablet:gap-4'}`}
    >
      {/* Заголовок не уже 320 px: много кнопок переносятся в две строки справа, а не сжимают сведения. */}
      <div className={`min-w-0 flex-1 ${stacked ? '' : 'tablet:min-w-[320px]'}`}>
        {titleAside ? (
          <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
            <h1 className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[22px] font-semibold tracking-[-0.01em] text-ink max-sm:text-[19px]">
              {title}
            </h1>
            {titleAside}
          </div>
        ) : (
          <h1 className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[22px] font-semibold tracking-[-0.01em] text-ink max-sm:text-[19px]">
            {title}
          </h1>
        )}
        {description && <p className="mt-1 text-[13.5px] text-ink-2">{description}</p>}
        {meta && <div className="mt-3 flex flex-wrap items-center gap-2">{meta}</div>}
      </div>
      {actions && (
        <div
          className={`flex min-w-0 flex-wrap items-center gap-2 ${stacked ? 'w-full' : 'justify-end pt-0.5 max-tablet:w-full max-tablet:justify-start max-tablet:pt-0'}`}
        >
          {actions}
        </div>
      )}
    </div>
  );
}

/* ---------- Правая панель (дизайн-система Drawer, specs/003-day-card-questions/contracts/ui-panel.md) ---------- */

/** Узкий экран — до 900 px включительно: панель на весь экран как модальный слой. */
const NARROW_QUERY = '(max-width: 900px)';

function readNarrow(): boolean {
  if (typeof window.matchMedia === 'function') return window.matchMedia(NARROW_QUERY).matches;
  return window.innerWidth <= 900; // jsdom: matchMedia нет
}

/** Подписка на ширину экрана: `resize` и смена медиазапроса. */
function subscribeWidth(query: string) {
  return (onChange: () => void): (() => void) => {
    window.addEventListener('resize', onChange);
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia(query) : null;
    mq?.addEventListener?.('change', onChange);
    return () => {
      window.removeEventListener('resize', onChange);
      mq?.removeEventListener?.('change', onChange);
    };
  };
}
const subscribeNarrow = subscribeWidth(NARROW_QUERY);

/** Экран до 900 px включительно (ступень «на весь экран» правой панели). */
export function useNarrowScreen(): boolean {
  return useSyncExternalStore(subscribeNarrow, readNarrow, () => false);
}

/**
 * 901–1239 px: левее открытой панели листу мало места, если меню открыто (меньше ~350 px), — меню на это время
 * сворачивается (`AppShell`). От 1240 px левее панели 620 px и меню 256 px остаётся не меньше 356 px.
 */
const OVER_SHEET_QUERY = '(min-width: 901px) and (max-width: 1239px)';
function readOverSheet(): boolean {
  if (typeof window.matchMedia === 'function') return window.matchMedia(OVER_SHEET_QUERY).matches;
  return window.innerWidth >= 901 && window.innerWidth <= 1239; // jsdom: matchMedia нет
}
const subscribeOverSheet = subscribeWidth(OVER_SHEET_QUERY);

/** Экран 901–1239 px: открытой правой панели нужно место — меню сворачивается. */
export function useOverSheetScreen(): boolean {
  return useSyncExternalStore(subscribeOverSheet, readOverSheet, () => false);
}

export interface PanelHost {
  /** Открыта правая панель справа (не развёрнутая): кнопки заголовка — под заголовком, у таблицы — место справа. */
  open: boolean;
  /** Панель сообщает, что открыта; возвращает функцию «закрыта». */
  register: () => () => void;
}

const PanelHostContext = createContext<PanelHost>({ open: false, register: () => () => {} });

/** Состояние места для правой панели — у листа табеля в `AppShell`: `open` — панель открыта справа. */
export function useSidePanelHost(): PanelHost {
  const [count, setCount] = useState(0);
  const register = useCallback(() => {
    setCount((c) => c + 1);
    return () => setCount((c) => c - 1);
  }, []);
  return useMemo(() => ({ open: count > 0, register }), [count, register]);
}

/** Передаёт место для правой панели содержимому листа (страницам с `SidePanel` и `PageHeader`). */
export function SidePanelHost({ value, children }: { value: PanelHost; children: ReactNode }) {
  return <PanelHostContext.Provider value={value}>{children}</PanelHostContext.Provider>;
}

/** Открыта ли правая панель справа (для раскладки листа: кнопки заголовка — под заголовком). */
export function useSidePanelOpen(): boolean {
  return useContext(PanelHostContext).open;
}

/**
 * Место справа у таблицы под правой панелью. Панель (дизайн-система Drawer, как в образце gr8r) — по умолчанию 620 px,
 * ширину человек меняет, перетаскивая левый край (`PanelResizer`, `--side-panel-w`); не уже 400 px, левее неё остаётся
 * не меньше 360 px листа. Панель лежит поверх листа и его не сдвигает; таблица получает справа место той же ширины,
 * чтобы последние дни можно было вывести из-под панели.
 */
export const SIDE_PANEL_ROOM = 'pr-[clamp(400px,var(--side-panel-w,620px),calc(100vw-360px))]';

/** Ширина панели, выбранная перетаскиванием края, — в браузере человека (как «Скрыть меню»). */
const PANEL_WIDTH_KEY = 'tabel.sidePanelWidth';
const PANEL_MIN = 400;
/** Ширина панели по умолчанию, px. */
const PANEL_DEFAULT = 620;
/** Левее панели остаётся не меньше этого листа, px. */
const SHEET_MIN = 360;
/** Шаг клавишами ← и →, px. */
const PANEL_STEP = 16;

function panelWidthLimits(): { min: number; max: number } {
  return { min: PANEL_MIN, max: Math.max(PANEL_MIN, window.innerWidth - SHEET_MIN) };
}

function readPanelWidth(): number | null {
  try {
    const v = Number(localStorage.getItem(PANEL_WIDTH_KEY));
    return Number.isFinite(v) && v >= PANEL_MIN ? Math.round(v) : null;
  } catch {
    return null; // хранилище недоступно — ширина по умолчанию
  }
}

/** Ширина в `--side-panel-w` у корня страницы: её читают панель и место справа у таблицы; `null` — по умолчанию. */
function applyPanelWidth(w: number | null) {
  if (w === null) document.documentElement.style.removeProperty('--side-panel-w');
  else document.documentElement.style.setProperty('--side-panel-w', `${w}px`);
}

function savePanelWidth(w: number | null) {
  try {
    if (w === null) localStorage.removeItem(PANEL_WIDTH_KEY);
    else localStorage.setItem(PANEL_WIDTH_KEY, String(w));
  } catch {
    // хранилище недоступно — ширина живёт до перезагрузки
  }
}

/**
 * Левый край правой панели: потянуть мышью или пальцем — ширина панели; ← и → — на 16 px, Home и End — шире и уже
 * всего; двойное нажатие — 620 px. Ширина запоминается в браузере (WAI-ARIA, «Window Splitter»).
 */
function PanelResizer({ panel }: { panel: RefObject<HTMLElement | null> }) {
  const [now, setNow] = useState<number | null>(null);
  // Тянут ли край сейчас и последняя ширина при перетаскивании — она и запоминается, когда край отпустили.
  const drag = useRef<{ on: boolean; last: number | null }>({ on: false, last: null });
  // После отрисовки: ссылка на панель появляется позже, чем срабатывают эффекты отрисовки её содержимого.
  useEffect(() => {
    setNow(panel.current?.offsetWidth || readPanelWidth());
  }, [panel]);
  const set = (w: number, save: boolean) => {
    const { min, max } = panelWidthLimits();
    const next = Math.round(Math.min(max, Math.max(min, w)));
    applyPanelWidth(next);
    setNow(next);
    drag.current.last = next;
    if (save) savePanelWidth(next);
  };
  const width = () => now ?? (panel.current?.offsetWidth || readPanelWidth() || PANEL_DEFAULT);
  const { min, max } = panelWidthLimits();
  // Край берёт фокус — `aria-valuenow` обязателен (WAI-ARIA, «Window Splitter»; axe `aria-required-attr`). Пока панель
  // не измерена — запомненная ширина или ширина по умолчанию.
  const valueNow = Math.min(max, Math.max(min, now ?? readPanelWidth() ?? PANEL_DEFAULT));
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Ширина панели"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={valueNow}
      tabIndex={0}
      data-panel-resizer
      title="Потяните, чтобы изменить ширину. Двойное нажатие — ширина по умолчанию"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        drag.current = { on: true, last: null };
        try {
          e.currentTarget.setPointerCapture?.(e.pointerId); // край не теряет указатель, если тянут быстро
        } catch {
          // указатель уже отпущен — тянем без захвата
        }
      }}
      onPointerMove={(e) => {
        if (drag.current.on) set(window.innerWidth - e.clientX, false);
      }}
      onPointerUp={(e) => {
        if (!drag.current.on) return;
        if (e.currentTarget.hasPointerCapture?.(e.pointerId))
          e.currentTarget.releasePointerCapture(e.pointerId);
        const last = drag.current.last;
        drag.current = { on: false, last: null };
        if (last !== null) savePanelWidth(last); // нажали без движения — ширина прежняя
      }}
      onDoubleClick={() => {
        savePanelWidth(null);
        applyPanelWidth(null);
        setNow(panel.current?.offsetWidth || null);
      }}
      onKeyDown={(e) => {
        const step = { ArrowLeft: PANEL_STEP, ArrowRight: -PANEL_STEP }[e.key];
        if (step !== undefined) set(width() + step, true);
        else if (e.key === 'Home') set(max, true);
        else if (e.key === 'End') set(min, true);
        else return;
        e.preventDefault();
      }}
      className="group absolute inset-y-0 left-0 z-10 w-2 cursor-col-resize touch-none outline-none"
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-0.5 bg-accent opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 group-active:opacity-100"
      />
    </div>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Открыт слой поверх панели — меню или окно: Esc и Tab принадлежат ему (дизайн-система, «Слои и Esc»). */
function upperLayerOpen(): boolean {
  return document.querySelector('[role="menu"], dialog[open]') !== null;
}

/** Tab по кругу внутри панели (модальный слой). */
function trapTab(e: KeyboardEvent, root: HTMLElement) {
  const items = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => !el.closest('[inert]'));
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  const inside = active instanceof Node && root.contains(active) && active !== root;
  if (!first || !last) {
    e.preventDefault();
    root.focus();
    return;
  }
  if (e.shiftKey) {
    if (!inside || active === first) {
      e.preventDefault();
      last.focus();
    }
  } else if (!inside || active === last) {
    e.preventDefault();
    first.focus();
  }
}

export type PanelMode = 'side' | 'expanded' | 'screen';

/**
 * Открытая правая панель — для тостов (`ToastProvider` стоит выше каркаса и места панели): тост не закрывает подвал
 * панели. `footer` — высота подвала, px (0 — подвала нет или не измерен).
 */
export interface PanelPresence {
  mode: PanelMode;
  footer: number;
}

let presence: PanelPresence | null = null;
let presenceOwner: object | null = null;
const presenceListeners = new Set<() => void>();
function announce(owner: object, p: PanelPresence | null) {
  if (p === null && presenceOwner !== owner) return; // уже открыта другая панель
  presenceOwner = p ? owner : null;
  presence = p;
  for (const l of presenceListeners) l();
}
function subscribePresence(onChange: () => void): () => void {
  presenceListeners.add(onChange);
  return () => presenceListeners.delete(onChange);
}

/** Открытая правая панель: режим и высота подвала; закрыта — `null`. */
export function useSidePanelPresence(): PanelPresence | null {
  return useSyncExternalStore(
    subscribePresence,
    () => presence,
    () => null,
  );
}

const PANEL_MODE: Record<PanelMode, string> = {
  // От 901 px: у правого края во всю высоту, поверх листа, без затемнения; тень — влево, на лист. Ширина — 620 px или
  // выбранная перетаскиванием левого края (`--side-panel-w`), не уже 400 px, левее — не меньше 360 px листа.
  side: 'top-0 right-0 bottom-0 z-[44] w-[clamp(400px,var(--side-panel-w,620px),calc(100vw-360px))] border-l border-line shadow-drawer',
  // «Развернуть»: по центру, до 880 px, с затемнением.
  expanded:
    'inset-x-0 top-[5vh] bottom-[5vh] z-[62] mx-auto w-[calc(100vw-32px)] max-w-[880px] rounded-sheet border border-line shadow-pop',
  // До 900 px: на весь экран поверх нижней навигации; кнопки шапки и подвала — не ниже 40 px (contracts/ui-panel.md,
  // «Узкий экран и телефон»), и на планшете, где у кнопок обычная высота.
  screen:
    'inset-0 z-[80] w-full shadow-pop [&>footer_button]:min-h-10 [&>header_button]:min-h-10 [&>header_button]:min-w-10',
};

/**
 * Правая панель (дизайн-система Drawer; contracts/ui-panel.md, «Режимы правой панели»): карточка дня, вопросы,
 * сотрудник, «Добавить людей». Без `<dialog>` и `showModal`.
 * - От 901 px — у правого края во всю высоту, 620 px, поверх листа без затемнения: лист не сдвигается, табель слева
 *   работает, нажатие на другую ячейку меняет содержимое. Левый край тянется (`PanelResizer`), ширина запоминается.
 * - До 900 px — на весь экран как модальный слой: `aria-modal`, Tab ходит по кругу.
 * - «Развернуть» — по центру, до 880 px, с затемнением и удержанием фокуса; нажатие на затемнение сворачивает.
 * - Esc и ✕ вызывают `onClose`; Esc не срабатывает, пока открыто меню или окно поверх. Защиту несохранённого
 *   страница добавляет сама: `onClose={() => guard(close)}` (`UnsavedGuard`).
 * - При открытии фокус — в панель (`initialFocus` или сама панель); при закрытии — на элемент вне панели,
 *   который был в фокусе последним (ячейка, открывшая день), или на `returnFocus()`.
 * Шапка 48 px: «Назад» · крошка «{crumb} / {title}» · `actions` · «Развернуть» · ✕. Под шапкой — `status`
 * (полоса состояния), тело с прокруткой, подвал `footer` с отступом safe-area.
 */
export function SidePanel({
  open,
  onClose,
  label,
  title,
  crumb,
  icon: Icon,
  onBack,
  actions,
  status,
  footer,
  children,
  expandable = true,
  initialFocus,
  returnFocus,
}: {
  open: boolean;
  /** ✕ и Esc. */
  onClose: () => void;
  /** Имя панели для экранного диктора: «Карточка дня», «Вопросы по табелю». */
  label: string;
  /** Текущий раздел в крошке (жирным): «Карточка дня». */
  title: ReactNode;
  /** Начало крошки: название табеля «Монолит-1». На телефоне скрыто. */
  crumb?: ReactNode;
  icon?: LucideIcon;
  /** Есть — в шапке кнопка «Назад». */
  onBack?: () => void;
  /** Кнопки шапки между крошкой и «Развернуть»: «3 из 9» ‹ ›, меню «…». */
  actions?: ReactNode;
  /** Полоса состояния под шапкой (не прокручивается). */
  status?: ReactNode;
  /** Подвал: кнопки действий. */
  footer?: ReactNode;
  children: ReactNode;
  /** Кнопка «Развернуть» (на широком экране). */
  expandable?: boolean;
  /** Куда поставить фокус при открытии; по умолчанию — сама панель. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Куда вернуть фокус при закрытии; по умолчанию — последний элемент вне панели, бывший в фокусе. */
  returnFocus?: () => HTMLElement | null | undefined;
}) {
  const narrow = useNarrowScreen();
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const mode: PanelMode = narrow ? 'screen' : expanded && expandable ? 'expanded' : 'side';
  const modal = mode !== 'side';

  // Обработчики документа читают последние значения без переподписки.
  const latest = useRef({ onClose, modal, returnFocus, initialFocus });
  latest.current = { onClose, modal, returnFocus, initialFocus };

  // Закрытая панель открывается снова справа.
  useEffect(() => {
    if (!open) setExpanded(false);
  }, [open]);

  // Лист знает, что панель открыта справа: кнопки заголовка — под заголовком, у таблицы — место справа.
  const { register } = useContext(PanelHostContext);
  const side = open && mode !== 'expanded';
  useEffect(() => (side ? register() : undefined), [side, register]);

  // Тосты знают, где панель и какой высоты её подвал: подвал не закрывается тостом.
  const footerRef = useRef<HTMLElement>(null);
  const [footerH, setFooterH] = useState(0);
  const hasFooter = Boolean(footer);
  // biome-ignore lint/correctness/useExhaustiveDependencies: подвал появляется и исчезает вместе с `hasFooter`
  useEffect(() => {
    const el = footerRef.current;
    if (!open || !el) {
      setFooterH(0);
      return;
    }
    const measure = () => setFooterH(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, hasFooter]);
  useEffect(() => {
    if (!open) return;
    const owner = {};
    announce(owner, { mode, footer: footerH });
    return () => announce(owner, null);
  }, [open, mode, footerH]);

  // Фокус: в панель при открытии, обратно — при закрытии.
  useEffect(() => {
    if (!open) return;
    const panel = ref.current;
    const active = document.activeElement;
    let back: HTMLElement | null = active instanceof HTMLElement && active !== document.body ? active : null;
    const onFocusIn = (e: FocusEvent) => {
      if (e.target instanceof HTMLElement && !panel?.contains(e.target)) back = e.target;
    };
    document.addEventListener('focusin', onFocusIn);
    (latest.current.initialFocus?.current ?? panel)?.focus({ preventScroll: true });
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      const now = document.activeElement;
      const lost = !now || now === document.body || !now.isConnected || (panel?.contains(now) ?? false);
      const target = latest.current.returnFocus?.() ?? back;
      if (lost && target?.isConnected) target.focus({ preventScroll: true });
    };
  }, [open]);

  // Esc закрывает; в модальном слое Tab ходит по кругу.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const panel = ref.current;
      if (!panel || e.defaultPrevented || upperLayerOpen()) return;
      if (e.key === 'Escape' && !e.isComposing) {
        e.preventDefault();
        latest.current.onClose();
      } else if (e.key === 'Tab' && latest.current.modal) {
        trapTab(e, panel);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useLayoutEffect(() => {
    if (open) applyPanelWidth(readPanelWidth());
  }, [open]);

  if (!open) return null;
  return createPortal(
    <>
      {mode === 'expanded' && (
        // biome-ignore lint/a11y/useKeyWithClickEvents: с клавиатуры — кнопка «Свернуть» в шапке и Esc
        <div
          data-panel-scrim
          aria-hidden
          onClick={() => setExpanded(false)}
          className="fixed inset-0 z-[61] bg-backdrop"
        />
      )}
      <section
        ref={ref}
        role="dialog"
        aria-modal={modal ? true : undefined}
        aria-label={label}
        tabIndex={-1}
        data-mode={mode}
        className={`fixed flex flex-col overflow-hidden bg-surface text-ink outline-none transition-[opacity,translate] duration-[340ms] ease-drawer starting:translate-x-12 starting:opacity-0 motion-reduce:transition-none ${PANEL_MODE[mode]}`}
      >
        {mode === 'side' && <PanelResizer panel={ref} />}
        <header className="flex h-12 shrink-0 items-center gap-0.5 border-b border-line pr-2.5 pl-4 text-[13px] text-ink-2 max-sm:pr-1 max-sm:pl-1">
          {onBack && <IconButton icon={ArrowLeft} label="Назад" onClick={onBack} />}
          {Icon && !onBack && (
            <Icon size={16} strokeWidth={1.75} className="mr-1.5 shrink-0 text-ink-3" aria-hidden />
          )}
          <div className="flex min-w-0 flex-1 items-center gap-1.5 whitespace-nowrap max-sm:pl-2">
            {crumb && (
              <>
                <span className="truncate max-sm:hidden">{crumb}</span>
                <span className="text-ink-3 max-sm:hidden" aria-hidden>
                  /
                </span>
              </>
            )}
            <span className="truncate font-medium text-ink">{title}</span>
          </div>
          {actions}
          {expandable && !narrow && (
            <IconButton
              icon={expanded ? Minimize2 : Maximize2}
              label={expanded ? 'Свернуть' : 'Развернуть'}
              onClick={() => setExpanded((v) => !v)}
            />
          )}
          <IconButton icon={X} label="Закрыть" title="Закрыть (Esc)" onClick={() => onClose()} />
        </header>
        {status}
        <div
          data-panel-body
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-x-hidden overflow-y-auto overscroll-contain px-6 pt-5 pb-6 max-sm:p-4"
        >
          {children}
        </div>
        {footer && (
          <footer
            ref={footerRef}
            className="flex shrink-0 flex-wrap items-center gap-2 border-t border-line bg-surface px-6 pt-2.5 pb-[calc(10px+env(safe-area-inset-bottom))] max-sm:px-4"
          >
            {footer}
          </footer>
        )}
      </section>
    </>,
    document.body,
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center max-sm:px-4 max-sm:py-10">
      <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-card bg-accent-soft text-accent">
        <Icon size={20} strokeWidth={1.75} aria-hidden />
      </span>
      <p className="text-[15px] font-semibold">{title}</p>
      {description && <p className="mt-1 max-w-md text-[13px] text-ink-2">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* ---------- Шаги, строка настройки, карточки-радио (дизайн-система Steps, SettingsRow, Choice; 007 T038) ----------
   Разметка — на готовых классах `components/bundle.css` (`t-steps`, `t-srow`, `t-radio`): `index.css` подключает их
   слоем `components`, поэтому утилиты Tailwind рядом с ними действуют. */

/**
 * Состояние шага (дизайн-система Steps): `todo` — не начат (номер с рамкой), `cur` — текущий (заливка акцентом),
 * `wait` — сервис ждёт сигнала (вращающаяся дуга), `ok` — выполнен (✓), `bad` — нужно исправить (!), `warn` — можно
 * идти дальше, но есть риск (!). Смысл состояния повторяет строка состояния словами.
 */
export type StepState = 'todo' | 'cur' | 'wait' | 'ok' | 'bad' | 'warn';

export interface StepItem {
  /**
   * Ключ шага для `open` и `onToggle`. Число — номер шага мастера: его показывает кружок. По умолчанию — номер по
   * порядку с 1.
   */
  key?: string | number;
  /** Название: «Шаг 4. Связь SmartPSS с сервисом». */
  title: ReactNode;
  state?: StepState;
  /** Строка состояния под названием — MUST: «Соединение есть», «Пароль не подошёл», «Откроется после шага 3». */
  status?: ReactNode;
  /** Бейдж рядом с названием: «по желанию». */
  badge?: ReactNode;
  /** Тело шага: что сделать, значения, итог проверки. Есть тело — заголовок раскрывает его нажатием. */
  body?: ReactNode;
}

const STEP_CLS: Record<StepState, string> = {
  todo: '',
  cur: 'is-cur',
  wait: 'is-wait',
  ok: 'is-ok',
  bad: 'is-bad',
  warn: 'is-warn',
};
/** Цвет строки состояния — цвет состояния; у текущего, ждущего и не начатого — `color-ink-2`. */
const STEP_STATUS_CLS: Record<StepState, string> = {
  todo: 'ss',
  cur: 'ss',
  wait: 'ss',
  ok: 'ss ok',
  bad: 'ss bad',
  warn: 'ss warn',
};

/** Знак в кружке шага: ✓ и ! — контуры из дизайн-системы (components/Steps/preview.html), размер задаёт `t-step-dot`. */
function StepMark({ mark }: { mark: 'check' | 'bang' }) {
  return (
    <svg className="t-ico" viewBox="0 0 24 24" aria-hidden="true" data-mark={mark}>
      {mark === 'check' ? (
        <path d="M20 6 9 17l-5-5" />
      ) : (
        <>
          <path d="M12 5v9" />
          <path d="M12 19h.01" />
        </>
      )}
    </svg>
  );
}

/**
 * Шаги процесса по порядку (дизайн-система Steps): номер в кружке и линия, состояние кружком и словами. Шаг с телом
 * раскрывается нажатием на заголовок (`aria-expanded`); тело свёрнутого шага не теряет введённого. Раскрытые шаги —
 * `open`; с `onToggle` ими управляет потребитель (открыт текущий шаг, человек вернулся на то же место), без него
 * `open` — начальное раскрытие. Шаги без тела — пронумерованный список: заголовок не кнопка, пояснение — строка
 * состояния. На телефоне раскладка та же, заголовок шага — цель нажатия 44 px (`t-step-h`).
 */
export function Steps({
  items,
  open,
  onToggle,
  label,
}: {
  items: readonly StepItem[];
  open?: Iterable<string | number>;
  onToggle?: (key: string | number) => void;
  /** Название списка для чтения с экрана: «Шаги подключения». */
  label?: string;
}) {
  const id = useId();
  const [own, setOwn] = useState<ReadonlySet<string | number>>(() => new Set(open ?? []));
  const opened = onToggle ? new Set(open ?? []) : own;
  const toggle = (key: string | number) => {
    if (onToggle) {
      onToggle(key);
      return;
    }
    setOwn((s) => {
      const next = new Set(s);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  };
  return (
    <ol className="t-steps" aria-label={label}>
      {items.map((s, i) => {
        const key = s.key ?? i + 1;
        const state = s.state ?? 'todo';
        const hasBody = s.body !== undefined && s.body !== null;
        const isOpen = hasBody && opened.has(key);
        const bodyId = `${id}-${i}`;
        const head = (
          <span>
            <span className="tt">{s.title}</span>
            {s.badge && (
              <>
                {' '}
                <Badge>{s.badge}</Badge>
              </>
            )}
            {s.status && <span className={STEP_STATUS_CLS[state]}>{s.status}</span>}
          </span>
        );
        return (
          <li
            key={key}
            className={['t-step', STEP_CLS[state], isOpen ? 'open' : ''].filter(Boolean).join(' ')}
          >
            <span className="t-step-dot" aria-hidden>
              {state === 'ok' ? (
                <StepMark mark="check" />
              ) : state === 'bad' || state === 'warn' ? (
                <StepMark mark="bang" />
              ) : typeof key === 'number' ? (
                key
              ) : (
                i + 1
              )}
            </span>
            <div>
              {hasBody ? (
                <button
                  type="button"
                  className="t-step-h"
                  aria-expanded={isOpen}
                  aria-controls={bodyId}
                  onClick={() => toggle(key)}
                >
                  {head}
                  <ChevronDown className="t-ico chev" aria-hidden />
                </button>
              ) : (
                // Не кнопка: раскрывать нечего (`t-step-h` задаёт раскладку и высоту строки).
                <div className="t-step-h cursor-default">{head}</div>
              )}
              {hasBody && (
                <div id={bodyId} className="t-step-b" hidden={!isOpen}>
                  {s.body}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Строка настройки (дизайн-система SettingsRow, `t-srow`): название и одна фраза-пояснение слева, выбор справа; на
 * телефоне выбор уходит под текст. `children` — выбор; функция получает id названия и пояснения, чтобы подписать
 * переключатель (`aria-labelledby`, `aria-describedby`).
 */
export function SettingsRow({
  title,
  description,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode | ((ids: { titleId: string; descriptionId: string | undefined }) => ReactNode);
}) {
  const id = useId();
  const titleId = `${id}-t`;
  const descriptionId = description ? `${id}-d` : undefined;
  const control = typeof children === 'function' ? children({ titleId, descriptionId }) : children;
  return (
    <div className="t-srow">
      <div>
        <div className="t" id={titleId}>
          {title}
        </div>
        {description && (
          <div className="d" id={descriptionId}>
            {description}
          </div>
        )}
      </div>
      {/* Пустое место справа держит сетку строки, когда выбора нет. */}
      {control ?? <span />}
    </div>
  );
}

export interface ModeOption<T extends string> {
  value: T;
  title: string;
  /** Пояснения под названием: что нужно и что получится. */
  lines: readonly ReactNode[];
}

/**
 * Карточки-радио (дизайн-система Choice: «если нужен образец — карточки с `role="radiogroup"`»; прототип, шаг 3
 * «Способ связи»): радио `t-radio`, название и пояснения; выбранная карточка — рамка `color-accent` и фон
 * `color-accent-soft`. Вся карточка — подпись радио: цель нажатия больше `size-tap`. Стрелки двигают выбор, как у
 * обычных радио одной группы. До 640 px — одна колонка.
 */
export function ModeCards<T extends string>({
  label,
  name,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  /** Имя группы радио. */
  name: string;
  value: T;
  options: readonly ModeOption<T>[];
  onChange: (value: T) => void;
  /** Закрытый шаг: выбор читается, но недоступен. */
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div role="radiogroup" aria-label={label} className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
      {options.map((o, i) => {
        const on = o.value === value;
        const titleId = `${id}-${i}-t`;
        const linesId = `${id}-${i}-d`;
        return (
          <label
            key={o.value}
            className={`flex items-start gap-2.5 rounded-control border px-3.5 py-3 transition-colors duration-150 ${
              on ? 'border-accent bg-accent-soft' : 'border-line bg-surface'
            } ${disabled ? 'cursor-not-allowed opacity-45' : `cursor-pointer ${on ? '' : 'hover:border-line-strong'}`}`}
          >
            <input
              type="radio"
              className="t-radio mt-[3px] shrink-0"
              name={name}
              value={o.value}
              checked={on}
              disabled={disabled}
              aria-labelledby={titleId}
              aria-describedby={o.lines.length > 0 ? linesId : undefined}
              onChange={() => onChange(o.value)}
            />
            <span className="min-w-0">
              <span id={titleId} className="block text-[14px] font-semibold text-ink">
                {o.title}
              </span>
              <span id={linesId}>
                {o.lines.map((line, k) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: пояснения карточки неизменны и без своих ключей
                  <span key={k} className="mt-1 block text-[13px] text-ink-2">
                    {line}
                  </span>
                ))}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

/** Инициалы в скруглённом квадрате (дизайн-система, Avatar). */
export function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');
  return (
    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-ok text-[13px] font-semibold text-white">
      {initials || '?'}
    </span>
  );
}

/** Пункты меню, по которым ходят ↑ ↓: обычные и выбор значения (дизайн-система, Dropdown). */
const MENU_ITEMS = '[role="menuitem"], [role="menuitemradio"]';

/**
 * Всплывающее меню (дизайн-система, Dropdown): белая карточка с тенью у кнопки-якоря.
 * Закрывается кликом мимо и Esc, ↑↓ двигают фокус по пунктам.
 */
export function Dropdown({
  trigger,
  children,
  side = 'bottom',
  align = 'start',
  width = 'w-full',
}: {
  trigger: (p: { open: boolean; toggle: () => void }) => ReactNode;
  children: (close: () => void) => ReactNode;
  side?: 'top' | 'bottom';
  /** `end` — меню прижато к правому краю кнопки (кнопка «…» в углу карточки). */
  align?: 'start' | 'end';
  width?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  /** Сдвиг меню по горизонтали, px: меню не выходит за край экрана (кнопка «…» у заголовка на телефоне). */
  const [shift, setShift] = useState(0);

  useLayoutEffect(() => {
    if (!open) {
      setShift(0);
      return;
    }
    const r = panel.current?.getBoundingClientRect();
    if (!r || r.width === 0) return;
    const gap = 8;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    if (r.right > vw - gap) setShift(Math.max(gap - r.left, vw - gap - r.right));
    else if (r.left < gap) setShift(gap - r.left);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const first = panel.current?.querySelector<HTMLElement>(`input, ${MENU_ITEMS}`);
    first?.focus();
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      wrap.current?.querySelector<HTMLElement>('button')?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [...(panel.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? [])];
    if (items.length === 0) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  return (
    <div ref={wrap} className="relative">
      {trigger({ open, toggle: () => setOpen((v) => !v) })}
      {open && (
        <div
          ref={panel}
          role="menu"
          onKeyDown={onKeyDown}
          style={shift ? { transform: `translateX(${shift}px)` } : undefined}
          className={`absolute ${align === 'end' ? 'right-0' : 'left-0'} z-40 ${side === 'top' ? 'bottom-full mb-2' : 'top-full mt-1.5'} ${width} max-w-[calc(100vw-32px)] overflow-hidden rounded-card border border-line bg-surface py-1 shadow-pop`}
        >
          {children(() => {
            // Пункт выбран — фокус на кнопку меню: окно, открытое пунктом, вернёт его туда же.
            setOpen(false);
            wrap.current?.querySelector<HTMLElement>('button')?.focus();
          })}
        </div>
      )}
    </div>
  );
}

export function MenuItem({
  icon: Icon,
  children,
  onClick,
  checked,
  external,
  danger,
}: {
  icon?: LucideIcon;
  children: ReactNode;
  onClick?: () => void;
  /**
   * Пункт выбора значения или контекста (дизайн-система, Dropdown): `menuitemradio`, `aria-checked`; у текущего
   * значения — галочка `color-accent` справа. Нет поля — обычный пункт `menuitem`.
   */
  checked?: boolean;
  external?: boolean;
  /** Опасный пункт: красный, последний, за разделителем (дизайн-система, Dropdown). */
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role={checked === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={checked}
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13.5px] transition-colors hover:bg-subtle focus:bg-subtle focus:outline-none max-sm:min-h-10 ${danger ? 'text-bad' : 'text-ink'}`}
    >
      {Icon && (
        <Icon
          size={16}
          strokeWidth={1.75}
          className={`shrink-0 ${danger ? 'text-bad' : 'text-ink-2'}`}
          aria-hidden
        />
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {checked && <Check size={16} strokeWidth={1.75} className="shrink-0 text-accent" aria-hidden />}
      {external && <ArrowUpRight size={14} strokeWidth={1.75} className="shrink-0 text-ink-3" aria-hidden />}
    </button>
  );
}

export function MenuSeparator() {
  return <hr className="my-1 border-line" />;
}

/**
 * Кнопка-переключатель с рамкой и стрелками ⇅ (дизайн-система, SwitcherButton). Название кнопки для чтения с экрана —
 * «<подпись>: <значение>»: «Объект: Сахалинская».
 */
export function SwitcherButton({
  icon: Icon,
  label,
  caption,
  open,
  onClick,
  compact = false,
}: {
  icon: LucideIcon;
  label: string;
  caption?: string;
  open: boolean;
  onClick: () => void;
  /** В шапке листа (служебное справа, например роль): одна строка, подпись `caption` — только для чтения с экрана. */
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={caption ? `${caption}: ${label}` : label}
      onClick={onClick}
      className={`flex items-center rounded-control border text-left transition-colors hover:bg-surface ${
        compact ? 'max-w-[240px] gap-1.5 bg-surface px-2.5 py-1' : 'w-full gap-2.5 bg-surface/70 px-2.5 py-2'
      } ${open ? 'border-line-strong bg-surface' : 'border-line'}`}
    >
      <Icon size={compact ? 14 : 16} strokeWidth={1.75} className="shrink-0 text-ink-2" aria-hidden />
      <span className="min-w-0 flex-1">
        {caption && !compact && <span className="block text-[11px] leading-tight text-ink-3">{caption}</span>}
        <span
          className={`block truncate font-medium text-ink ${compact ? 'text-[12.5px]' : 'text-[13.5px]'}`}
        >
          {label}
        </span>
      </span>
      <ChevronsUpDown
        size={compact ? 14 : 16}
        strokeWidth={1.75}
        className="shrink-0 text-ink-3"
        aria-hidden
      />
    </button>
  );
}

/**
 * Метка «ключ: значение» для строки сведений под заголовком. `warn` — цвет «внимание»: «Данные СКУД» при тишине
 * источника (FR-039); смысл цвета дублирует подсказка `title` и плашка над табелем.
 */
export function MetaChip({
  icon: Icon,
  label,
  children,
  tone,
  title,
}: { icon: LucideIcon; label: string; children: ReactNode; tone?: 'warn'; title?: string }) {
  const warn = tone === 'warn';
  return (
    <span
      title={title}
      data-tone={tone}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12.5px] ${warn ? 'border-warn/25 bg-warn-soft text-warn' : 'border-line bg-surface text-ink-2'}`}
    >
      <Icon size={14} strokeWidth={1.75} className={warn ? 'text-warn' : 'text-ink-3'} aria-hidden />
      <span className={warn ? 'text-warn' : 'text-ink-3'}>{label}</span>
      <span className="font-medium text-ink">{children}</span>
    </span>
  );
}

const CALLOUT: Record<Tone, string> = {
  neutral: 'border-line bg-subtle text-ink-2',
  accent: 'border-accent/20 bg-accent-soft text-ink',
  ok: 'border-ok/20 bg-ok-soft text-ink',
  warn: 'border-warn/25 bg-warn-soft text-ink',
  bad: 'border-bad/20 bg-bad-soft text-ink',
};
const CALLOUT_ICON: Record<Tone, string> = {
  neutral: 'text-ink-3',
  accent: 'text-accent',
  ok: 'text-ok',
  warn: 'text-warn',
  bad: 'text-bad',
};

/** Подсказка о состоянии экрана: иконка и одна-две строки текста. */
export function Callout({
  tone = 'neutral',
  icon: Icon,
  children,
}: { tone?: Tone; icon: LucideIcon; children: ReactNode }) {
  return (
    <div
      className={`flex items-start gap-2.5 rounded-control border px-3.5 py-2.5 text-[13px] ${CALLOUT[tone]}`}
    >
      <Icon size={16} strokeWidth={1.75} className={`mt-px shrink-0 ${CALLOUT_ICON[tone]}`} aria-hidden />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/* ---------- Сегменты и полоса показателей (дизайн-система Segmented, StatStrip) ---------- */

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Счётчик серым внутри сегмента: «С вопросами 3». */
  count?: number;
  title?: string;
  disabled?: boolean;
}

/**
 * Сегментированный переключатель для 2–5 взаимоисключающих значений (дизайн-система, Segmented): дорожка `canvas`,
 * выбранный — белый с тонкой рамкой. Вид, период, быстрый фильтр («Все люди · С вопросами»), код отсутствия.
 * Применяется сразу, без тоста. `role="tablist"`, `aria-selected`; ← → Home End двигают выбор, Tab — только на
 * выбранный. На телефоне сегмент не ниже 40 px; `touch="tablet"` — до 900 px (в правой панели на весь экран, T103).
 * Больше 5 вариантов — выпадающий список.
 */
export function Segmented<T extends string>({
  label,
  value,
  onChange,
  options,
  className = '',
  touch = 'phone',
}: {
  /** Имя группы для экранного диктора: «Строки», «Код отсутствия». */
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: readonly SegmentOption<T>[];
  className?: string;
  /** До какой ширины сегмент не ниже 40 px: телефон (до 640 px) или планшет (до 900 px). */
  touch?: 'phone' | 'tablet';
}) {
  const list = useRef<HTMLDivElement>(null);
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const enabled = options.filter((o) => !o.disabled);
    const i = enabled.findIndex((o) => o.value === value);
    const n = enabled.length;
    const step: Record<string, number | undefined> = {
      ArrowRight: (i + 1) % n,
      ArrowDown: (i + 1) % n,
      ArrowLeft: (i - 1 + n) % n,
      ArrowUp: (i - 1 + n) % n,
      Home: 0,
      End: n - 1,
    };
    const k = step[e.key];
    const to = k === undefined ? undefined : enabled[k];
    if (!to) return;
    e.preventDefault();
    onChange(to.value);
    const tabs = [...(list.current?.querySelectorAll<HTMLElement>('[role="tab"]') ?? [])];
    tabs[options.indexOf(to)]?.focus();
  };
  return (
    <div
      ref={list}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={`inline-flex max-w-full gap-0.5 rounded-control bg-canvas p-0.5 ${className}`}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            title={o.title}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            className={`inline-flex h-6 min-w-0 items-center justify-center gap-1.5 rounded-[6px] px-2.5 text-[12.5px] font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50 max-sm:min-h-10 max-sm:flex-1 max-sm:px-3 ${touch === 'tablet' ? 'max-tablet:min-h-10 max-tablet:px-3' : ''} ${
              on ? 'bg-surface text-ink shadow-card ring-1 ring-line' : 'text-ink-2 hover:text-ink'
            }`}
          >
            {o.label}
            {o.count !== undefined && (
              <>
                {' '}
                <span className="text-[11px] text-ink-3 tabular-nums">{o.count}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

export interface Stat {
  key: string;
  icon?: LucideIcon;
  /** Подпись: «Без ответа». */
  label: string;
  /** Крупное число. Числа считает сервер или движок — интерфейс только показывает. */
  value: ReactNode;
  /** «из M» мелко рядом с числом. */
  of?: ReactNode;
  /** Строка пояснения: «список откроется справа». */
  note?: ReactNode;
  noteTone?: 'ok' | 'warn' | 'bad';
  /** Проблемное число — `bad`, хорошее — `ok`. */
  valueTone?: 'ok' | 'bad';
  /** Доля 0…1 — полоса прогресса в строке пояснения. */
  progress?: number;
  /** Открыть список с этим фильтром. Без обработчика показатель — не кнопка. */
  onClick?: () => void;
}

const STAT_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4 max-tablet:grid-cols-2 @max-[560px]:grid-cols-2',
};
const NOTE_TONE = { ok: 'text-ok', warn: 'text-warn', bad: 'text-bad' } as const;
const VALUE_TONE = { ok: 'text-ok', bad: 'text-bad' } as const;

function StatBody({ s }: { s: Stat }) {
  return (
    <>
      <span className="flex items-center gap-1.5 text-[13px] text-ink-2">
        {s.icon && <s.icon size={16} strokeWidth={1.75} className="shrink-0 text-ink-3" aria-hidden />}
        {s.label}
      </span>
      <span className="flex items-baseline gap-1">
        <span
          className={`text-[22px] leading-[1.1] font-semibold tracking-[-0.02em] tabular-nums ${s.valueTone ? VALUE_TONE[s.valueTone] : 'text-ink'}`}
        >
          {s.value}
        </span>
        {s.of !== undefined && (
          <>
            {' '}
            <small className="text-[13px] text-ink-3">из {s.of}</small>
          </>
        )}
      </span>
      {(s.note !== undefined || s.progress !== undefined) && (
        <span className="flex items-center gap-2 text-[12px] text-ink-3">
          {s.progress !== undefined && (
            <span className="h-1 min-w-12 flex-1 overflow-hidden rounded-full bg-canvas" aria-hidden>
              <span
                data-progress
                className="block h-full origin-left rounded-full bg-ok transition-transform duration-300 ease-out"
                style={{ transform: `scaleX(${Math.min(1, Math.max(0, s.progress))})` }}
              />
            </span>
          )}
          {s.note !== undefined && (
            <span className={s.noteTone ? NOTE_TONE[s.noteTone] : undefined}>{s.note}</span>
          )}
        </span>
      )}
    </>
  );
}

/**
 * Полоса из 3–4 показателей в общей рамке (дизайн-система, StatStrip): подпись с иконкой → крупное число →
 * строка пояснения. Каждый показатель — ссылка в список с этим фильтром («Без ответа 5» открывает вопросы без
 * ответа). Четыре показателя — сеткой 2 × 2 до 900 px и на листе уже 560 px (правая панель открыта).
 */
export function StatStrip({ label, items }: { label: string; items: readonly Stat[] }) {
  return (
    <div role="group" aria-label={label} className="@container">
      <div
        className={`grid gap-px overflow-hidden rounded-card border border-line bg-line ${STAT_COLS[items.length] ?? STAT_COLS[4]}`}
      >
        {items.map((s) => {
          const cell = 'flex min-w-0 flex-col gap-1.5 bg-surface px-4 py-3.5 text-left max-sm:px-3';
          return s.onClick ? (
            <button
              key={s.key}
              type="button"
              onClick={s.onClick}
              className={`${cell} transition-colors hover:bg-subtle focus-visible:outline-offset-[-2px]`}
            >
              <StatBody s={s} />
            </button>
          ) : (
            <div key={s.key} className={cell}>
              <StatBody s={s} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Вкладки и заглушка загрузки (дизайн-система Tabs, Skeleton) ---------- */

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
}

/**
 * Вкладки разделов одного объекта (дизайн-система, Tabs): активная — текст `ink` и подчёркивание 2 px `ink`,
 * остальные — `ink-2`. На узком экране вкладки — лента с прокруткой вбок без переноса строки; страница вбок не
 * прокручивается. `role="tablist"`, `aria-selected`; ← → Home End двигают выбор, Tab — только на выбранную. На
 * телефоне вкладка не ниже 40 px. `idPrefix` связывает вкладку с её панелью: `{idPrefix}-tab-{value}` и
 * `{idPrefix}-panel-{value}`.
 */
export function Tabs<T extends string>({
  label,
  value,
  onChange,
  items,
  idPrefix,
}: {
  /** Имя группы для экранного диктора: «Виды». */
  label: string;
  value: T;
  onChange: (value: T) => void;
  items: readonly TabItem<T>[];
  idPrefix: string;
}) {
  const list = useRef<HTMLDivElement>(null);
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const i = items.findIndex((t) => t.value === value);
    const n = items.length;
    const step: Record<string, number | undefined> = {
      ArrowRight: (i + 1) % n,
      ArrowLeft: (i - 1 + n) % n,
      Home: 0,
      End: n - 1,
    };
    const k = step[e.key];
    const to = k === undefined ? undefined : items[k];
    if (k === undefined || !to) return;
    e.preventDefault();
    onChange(to.value);
    list.current?.querySelectorAll<HTMLElement>('[role="tab"]')[k]?.focus();
  };
  return (
    <div
      ref={list}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="flex max-w-full gap-1 overflow-x-auto border-b border-line [scrollbar-width:none]"
    >
      {items.map((t) => {
        const on = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${t.value}`}
            aria-controls={`${idPrefix}-panel-${t.value}`}
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(t.value)}
            className={`relative inline-flex h-9 shrink-0 items-center gap-1.5 px-3 text-[13.5px] font-medium whitespace-nowrap transition-colors after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full max-sm:min-h-10 ${
              on ? 'text-ink after:bg-ink' : 'text-ink-2 hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Действие-ссылка и подтверждение с вводом названия (007 T041, T055) ---------- */

/**
 * Действие-ссылка в подсказке `Callout` и в тексте (дизайн-система Callout: «Действие — ссылкой в конце»: «Проверить
 * снова», «Скопировать пароль»). Это кнопка: действие меняет состояние, а не открывает адрес. Вид — ссылка `color-accent`
 * (как `.t-callout a`); на телефоне цель нажатия не меньше `size-tap`.
 */
export function LinkButton({ className = '', children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-1 rounded-xs text-left font-medium text-accent underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-ink-3 disabled:no-underline max-sm:min-h-10 ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/**
 * Подтверждение с вводом названия (дизайн-система ConfirmDialog: «поле «Введите название «Север», чтобы подтвердить».
 * Кнопка недоступна до точного совпадения, фокус в поле»). Окно-вопрос без крестика, последствия — `description`.
 * Название сравнивается без пробелов по краям, с учётом регистра. Enter в поле подтверждает, если название совпало.
 */
export function ConfirmNameDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  name,
  fieldLabel,
  confirmLabel,
  busy,
  icon = TriangleAlert,
}: {
  open: boolean;
  onClose: () => void;
  /** Получает введённое название (без пробелов по краям). */
  onConfirm: (typed: string) => void;
  title: string;
  description: ReactNode;
  /** Название, которое нужно ввести. */
  name: string;
  fieldLabel: string;
  confirmLabel: string;
  busy?: boolean;
  icon?: LucideIcon;
}) {
  const id = useId();
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);
  const value = typed.trim();
  const match = value === name.trim() && value !== '';
  const confirm = () => {
    if (match && !busy) onConfirm(value);
  };
  return (
    <AlertDialog open={open} onClose={onClose} title={title} description={description} icon={icon}>
      <div className="mb-3 basis-full">
        <label htmlFor={`${id}-name`} className="mb-1.5 block text-[13px] font-medium text-ink">
          {fieldLabel}
        </label>
        <input
          id={`${id}-name`}
          className={inputCls}
          value={typed}
          placeholder={name}
          autoComplete="off"
          spellCheck={false}
          data-autofocus
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            confirm();
          }}
        />
      </div>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="danger" disabled={!match || busy} onClick={confirm}>
        {confirmLabel}
      </Button>
    </AlertDialog>
  );
}

/**
 * Заглушка загрузки в форме будущего содержимого (дизайн-система, Skeleton): полоса `line` с мерцанием. Только при
 * настоящем ожидании. Контейнер заглушки страница помечает `aria-busy="true"` и `aria-label="Загрузка…"`.
 */
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={`block animate-pulse rounded-[6px] bg-line motion-reduce:animate-none ${className}`}
    />
  );
}
